import React from 'react';
import { Trees, Droplets, Sun, Home, CloudRain, Maximize2, Info } from 'lucide-react';
import { Button } from './ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";

const services = [
  {
    id: 'lawn-care',
    name: 'Lawn Care',
    icon: Trees,
    description: 'Professional mowing, edging, and lawn maintenance (up to 1000m²). Pricing can be altered depending on which machine we use - push mower or zero turn.',
    minimumCharge: '$85 + GST',
    minimumNote: 'If lawn is under 305m² it will fall into our minimum charge of $85.',
    hasCalculator: true,
    calculatorType: 'area',
    rate: 0.28,
    minimumValue: 85,
    minimumArea: 305,
    maxArea: 1000,
    gstInclusive: false,
    image: 'https://images.unsplash.com/photo-1635861347132-f282b945be7b?auto=format&fit=crop&q=80&w=400',
  },
  {
    id: 'lawn-care-rideon',
    name: 'Lawn Care (Ride On)',
    icon: Trees,
    description: 'Ride on mowing for larger properties over 1000m². We will use Google Earth to measure and contact you with a quote.',
    minimumCharge: 'Quote on request',
    minimumNote: 'We will measure your property using Google Earth and be in contact regarding your quote.',
    hasCalculator: false,
    gstInclusive: false,
    image: 'https://images.unsplash.com/photo-1734303023481-7508b5c9f1ff?auto=format&fit=crop&q=80&w=400',
  },
  {
    id: 'pressure-cleaning',
    name: 'Pressure Cleaning',
    icon: Droplets,
    description: 'Driveways, paths, patios, and outdoor surfaces',
    minimumCharge: '$200',
    hasCalculator: true,
    calculatorType: 'area',
    rate: 3,
    minimumValue: 200,
    gstInclusive: false,
    image: 'https://customer-assets.emergentagent.com/job_empire-lawn-quote/artifacts/9ken1myq_Pressure%20cleaning.jpg',
  },
  {
    id: 'solar-panel-clean',
    name: 'Solar Panel Clean',
    icon: Sun,
    description: 'Safe and efficient solar panel cleaning',
    minimumCharge: '$200 inc. GST',
    hasCalculator: true,
    calculatorType: 'panels',
    rate: 18,
    minimumValue: 200,
    gstInclusive: true,
    image: 'https://customer-assets.emergentagent.com/job_empire-lawn-quote/artifacts/3g3eshmf_565139083_783042874608389_4236596088548658646_n.jpg',
  },
  {
    id: 'roof-clean',
    name: 'Roof Clean',
    icon: Home,
    description: 'Tile and metal roof cleaning and moss removal',
    minimumCharge: '$200 inc. GST',
    hasCalculator: true,
    calculatorType: 'area',
    rate: 4,
    minimumValue: 200,
    gstInclusive: true,
    image: 'https://images.unsplash.com/photo-1726589004565-bedfba94d3a2?auto=format&fit=crop&q=80&w=400',
  },
  {
    id: 'gutter-clean',
    name: 'Gutter Clean',
    icon: CloudRain,
    description: 'Gutter clearing and downpipe flushing',
    minimumCharge: '$200 inc. GST',
    hasCalculator: true,
    calculatorType: 'length',
    rate: 5,
    minimumValue: 200,
    gstInclusive: true,
    image: 'https://images.unsplash.com/photo-1722253358715-61e69ef7b5a1?auto=format&fit=crop&q=80&w=400',
  },
  {
    id: 'exterior-window-clean',
    name: 'Exterior Window Clean',
    icon: Maximize2,
    description: 'Window panes and fly screen cleaning',
    minimumCharge: '$200',
    hasCalculator: true,
    calculatorType: 'windows',
    paneRate: 8,
    screenRate: 4,
    minimumValue: 200,
    gstInclusive: false,
    image: 'https://images.unsplash.com/photo-1641679162921-fdbf65a03f73?auto=format&fit=crop&q=80&w=400',
  },
];

export default function Services({ onServiceSelect }) {
  return (
    <section id="individual-services" className="py-20 bg-white" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center bg-amber-100 rounded-full px-4 py-2 mb-4">
            <span className="text-amber-800 font-inter text-sm font-medium">
              One-Off Service Quotes
            </span>
          </div>
          <h2 className="font-oswald text-3xl sm:text-4xl font-bold text-green-900 mb-4">
            Individual Services
          </h2>
          <div className="gold-accent-line w-24 mx-auto mb-6" />
          <p className="font-inter text-gray-600 max-w-2xl mx-auto">
            Not ready for a subscription? Use our calculators to get an instant minimum quote for individual services. 
            We'll follow up with a customised quote based on your property.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((service, index) => {
            const IconComponent = service.icon;
            return (
              <div
                key={service.id}
                className={`service-card bg-white rounded-xl overflow-hidden shadow-md border border-gray-100 animate-fade-in-up animation-delay-${(index % 4) + 1}00`}
                data-testid={`service-card-${service.id}`}
              >
                {/* Service Image */}
                <div className="h-40 overflow-hidden relative">
                  <img
                    src={service.image}
                    alt={service.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                  <div className="absolute bottom-3 left-3">
                    <div className="bg-white/90 backdrop-blur-sm p-2 rounded-lg">
                      <IconComponent className="text-green-900" size={24} />
                    </div>
                  </div>
                </div>

                {/* Service Content */}
                <div className="p-5">
                  <h3 className="font-oswald text-xl font-semibold text-gray-900 mb-2">
                    {service.name}
                  </h3>
                  <p className="font-inter text-sm text-gray-600 mb-4">
                    {service.description}
                  </p>

                  {/* Pricing Info */}
                  <div className="bg-slate-50 rounded-lg p-3 mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-inter text-xs text-gray-500 uppercase tracking-wide">
                        Pricing
                      </span>
                      {service.hasCalculator && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button className="text-amber-600 hover:text-amber-700" data-testid={`info-btn-${service.id}`}>
                                <Info size={16} />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs bg-gray-900 text-white p-3">
                              <p className="text-sm">Use Google Earth's measuring tool to measure your property</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </div>
                    <p className="font-inter text-sm text-amber-700">
                      Minimum charge = {service.minimumCharge}
                    </p>
                    {service.minimumNote && (
                      <p className="font-inter text-xs text-gray-500 mt-1 italic">
                        {service.minimumNote}
                      </p>
                    )}
                  </div>

                  {/* CTA Button */}
                  <Button
                    onClick={() => onServiceSelect(service)}
                    className="w-full bg-green-900 hover:bg-green-800 text-white font-oswald font-medium"
                    data-testid={`select-service-${service.id}`}
                  >
                    Get Quote
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Disclaimer */}
        <div className="mt-12 text-center">
          <div className="inline-flex items-center bg-amber-50 border border-amber-200 rounded-lg px-6 py-4">
            <Info className="text-amber-600 mr-3 flex-shrink-0" size={20} />
            <p className="font-inter text-sm text-amber-800">
              All quotes are subject to revision, we use Google Earth to do our measurements.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

export { services };
